import { queryOptions } from '@tanstack/react-query';
import invariant from 'tiny-invariant';

import { captureAnalyticsEvent } from './analytics/analytics-events.js';

export const auth0UserQueryKey = ['auth0-user'];

/**
 * Query options for fetching the Auth0 user. Example usage:
 *
 * ```ts
 * const { data: user } = useSuspenseQuery(auth0UserQueryOptions);
 * ```
 */
export const auth0UserQueryOptions = queryOptions({
	queryKey: auth0UserQueryKey,
	queryFn: async ({ meta }) => {
		invariant(meta !== undefined, 'Auth0Client not provided to query meta');

		// Don't bother checking if the user is authenticated if we're on the `logged-out` or `verify-email` page
		// users do not have sessions on these pages
		if (/^\/(logged-out|verify-email)\/?$/.test(window.location.pathname)) {
			return null;
		}

		const { auth0Client } = meta;

		try {
			await auth0Client.getTokenSilently();
		} catch (error) {
			// `getTokenSilently()` throws if the user is not authenticated. login_required and
			// missing_refresh_token are the expected codes for "no session"; anything else is signal
			// (iframe silent auth blocked by 3p-cookie policy, refresh token revoked, etc.).
			const errorCode = (error as { error?: unknown }).error;
			const errorDescription = (error as { error_description?: unknown }).error_description;
			const isExpected = errorCode === 'login_required' || errorCode === 'missing_refresh_token';
			captureAnalyticsEvent('auth_get_token_silently_failed', {
				error_code: typeof errorCode === 'string' ? errorCode : 'unknown',
				error_description: typeof errorDescription === 'string' ? errorDescription : undefined,
				is_expected: isExpected,
				cookie_enabled: navigator.cookieEnabled,
			});
			return null;
		}

		const user = (await auth0Client.getUser()) ?? null;
		return user;
	},
});
